import { AxiosProgressEvent, CancelTokenSource } from 'axios';
import * as t from 'io-ts';

import { CodecUtils } from 'common/utils/codec-utils';
import { RequestExecuter } from './request-executer';

const BASE_API_PATH = '/api';

const apiExecuter = new RequestExecuter(true);
const rootExecuter = new RequestExecuter(true);

export class CommonApi {
  /**
   * @deprecated Use getV instead
   */
  public static get<TResponse = any>(
    url: string,
    error: string = null,
  ): Promise<TResponse> {
    return apiExecuter.makeRequest<TResponse>({ method: 'get', url: `${BASE_API_PATH}${url}` }, error);
  }

  public static getV<TCodec extends t.Any>(
    url: string,
    codec: TCodec,
    error: string = null,
  ): Promise<t.TypeOf<TCodec>> {
    return CommonApi.get(url, error)
      .then(r => CommonApi.validateResponse(r, codec, 'get', url));
  }

  public static getBinary<TResponse = any>(
    url: string,
    error: string = null,
    cancelTokenSource?: CancelTokenSource,
  ): Promise<TResponse> {
    return apiExecuter.makeRequest<TResponse>(
      {
        method: 'get',
        url: `${BASE_API_PATH}${url}`,
        responseType: 'arraybuffer',
        cancelToken: cancelTokenSource?.token,
      },
      error,
    );
  }

  public static post<TResponse = any, TPayload = any>(
    url: string,
    data?: TPayload,
    error: string = null,
  ): Promise<TResponse> {
    return apiExecuter.makeRequest<TResponse>({ method: 'post', url: `${BASE_API_PATH}${url}`, data }, error);
  }

  public static postV<TResponseValidator extends t.Any, TPayload = any>(
    url: string,
    data?: TPayload,
    responseValidator?: TResponseValidator,
    error?: string,
  ): Promise<t.TypeOf<TResponseValidator>> {
    return CommonApi.post(url, data, error)
      .then(r => CommonApi.validateResponse(r, responseValidator, 'post', url));
  }

  public static put<TResponse = any, TPayload = any>(
    url: string,
    data: TPayload,
    error: string = null,
  ): Promise<TResponse> {
    return apiExecuter.makeRequest<TResponse>({ method: 'put', url: `${BASE_API_PATH}${url}`, data }, error);
  }

  public static putV<TResponseValidator extends t.Any, TPayload = any>(
    url: string,
    data?: TPayload,
    responseValidator?: TResponseValidator,
    error?: string,
  ): Promise<t.TypeOf<TResponseValidator>> {
    return CommonApi.put(url, data, error)
      .then(r => CommonApi.validateResponse(r, responseValidator, 'put', url));
  }

  public static delete<TResponse = any, TPayload = any>(
    url: string,
    data: TPayload = null,
    error: string = null,
  ): Promise<TResponse> {
    return apiExecuter.makeRequest<TResponse>({ method: 'delete', url: `${BASE_API_PATH}${url}`, data }, error);
  }

  public static deleteV<TResponseValidator extends t.Any, TPayload = any>(
    url: string,
    data: TPayload = null,
    responseValidator?: TResponseValidator,
    error: string = null,
  ): Promise<t.TypeOf<TResponseValidator>> {
    return CommonApi.delete(url, data, error)
      .then(r => CommonApi.validateResponse(r, responseValidator, 'delete', url));
  }

  public static patch<TResponse = any, TPayload = any>(
    url: string,
    data: TPayload,
    error: string = null,
  ): Promise<TResponse> {
    return apiExecuter.makeRequest<TResponse>({ method: 'patch', url: `${BASE_API_PATH}${url}`, data }, error);
  }

  public static patchV<TResponseValidator extends t.Any, TPayload = any>(
    url: string,
    data: TPayload,
    responseValidator?: TResponseValidator,
    error?: string,
  ): Promise<t.TypeOf<TResponseValidator>> {
    return CommonApi.patch(url, data, error)
      .then(r => CommonApi.validateResponse(r, responseValidator, 'patch', url));
  }

  public static uploadFile<T = any>(
    url: string,
    file: File,
    onProgress: (loaded: number) => void,
    onSuccess: (data: T) => void,
    onError: (err: any) => void,
    cancelTokenSource?: CancelTokenSource,
  ): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);
    return apiExecuter
      .makeRequest<T>({
        method: 'post',
        url: `${BASE_API_PATH}${url}`,
        data: formData,
        onUploadProgress: (e: AxiosProgressEvent) => {
          if (e.total && onProgress) {
            onProgress(e.loaded / e.total);
          }
        },
        cancelToken: cancelTokenSource?.token,
      })
      .then(data => {
        if (onSuccess) {
          onSuccess(data);
        }
      })
      .catch(err => {
        onError(err);
      });
  }

  public static downloadFile(
    url: string,
    error: string = null,
  ): Promise<void> {
    return CommonApi.makeFileRequest('get', url, null, error);
  }

  public static downloadFileWithData<TPayload = any>(
    url: string,
    data: TPayload,
    error: string = null,
  ): Promise<void> {
    return CommonApi.makeFileRequest('post', url, data, error);
  }

  public static getBlob<T = any>(
    url: string,
    error: string = null,
    cancelTokenSource?: CancelTokenSource,
  ): Promise<T> {
    return rootExecuter.makeRequest<T>(
      {
        method: 'get',
        url,
        responseType: 'blob',
        cancelToken: cancelTokenSource?.token,
      },
      error,
    );
  }

  public static getLink(url: string): string {
    return `${BASE_API_PATH}${url}`;
  }

  private static validateResponse<TResponseValidator extends t.Any>(
    response: t.TypeOf<TResponseValidator> | null,
    responseValidator: TResponseValidator | null,
    method: string,
    url: string,
  ): t.TypeOf<TResponseValidator> {
    if (response && responseValidator) {
      try {
        return CodecUtils.decode(response, responseValidator);
      } catch (error) {
        const message = `Invalid API response (method: ${method}, url: ${url}).\n ${error}`;
        console.error(message);
        throw Error(message);
      }
    }

    return response;
  }

  private static async makeFileRequest<K = any>(
    method: 'get' | 'post',
    url: string,
    data: K,
    error: string = null,
  ): Promise<void> {
    try {
      const response = await apiExecuter.makeRawRequest<Blob>({
        method,
        url: `${BASE_API_PATH}${url}`,
        responseType: 'blob',
        data,
      });

      if (response.status === 200) {
        let fileName = '';
        const matches = response.headers['content-disposition']?.match(/filename=((['"]).*?\2|[^;\n]*)/);
        if (matches && matches[1]) {
          fileName = matches[1].replace(/['"]/g, '');
        } else {
          throw new Error('Cannot parse Content-Disposition header.');
        }
        const fileUrl = window.URL.createObjectURL(new Blob([response.data], { type: response.data.type }));
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(fileUrl);
      }
    } catch (_error) {
      throw new Error(error ? `${error}` : String(_error));
    }
  }
}


export function baseGetJson(path: string, error: string = null): any {
  return CommonApi.get(path, error);
}

export function basePostJson(path: string, data: any, error: string = null): any {
  return CommonApi.post(path, data, error);
}

export function basePost(path: string, data: any = null, error: any = ''): any {
  return CommonApi.post(path, data, error);
}


export function basePutJson(path: string, data: any, error: any = ''): any {
  return CommonApi.put(path, data, error);
}

export function baseRemoveJson(path: any, error: any = ''): any {
  return CommonApi.delete(path, null, error);
}

export function baseDelete(path: any, body: any, error: any = ''): any {
  return CommonApi.delete(path, body, error);
}
